import { useMemo } from "react";
import {
  useBudgetMeta,
  useBudgetServicesState,
  useBudgetSuppliersState,
} from "../../../../contexts/BudgetContext/projectbudgets.context";
import { isSupplierVisibleInVersion } from "../../../../utils/budget-stage-visibility";
import { AddedSupplier } from "../../../../types/budget/budget";

/**
 * Id estável de uma linha de fornecedor, usado como `key` do React e como id do
 * sortable. Prefere `uid` (gravado em `ADD_SUPPLIER` e retrofitado ao carregar).
 * O fallback por índice só existe como rede de segurança — `supplierId` nasce
 * vazio e pode repetir entre linhas, então ele sozinho nunca serve.
 */
export function getSupplierRowId(
  supplier: { uid?: string; supplierId: string },
  absoluteIndex: number
): string {
  return supplier.uid ?? `supplier-row-${supplier.supplierId}-${absoluteIndex}`;
}

export type VisibleSupplierRow = {
  el: AddedSupplier;
  /** Índice ABSOLUTO em `supplierList` — é o que o reducer espera. */
  absoluteIndex: number;
};

/**
 * Linhas de fornecedor efetivamente exibidas na forma de cobrança e na etapa
 * atuais, cada uma carregando seu índice ABSOLUTO em `supplierList`.
 *
 * A tela mostra uma sublista (filtrada por calcOption + visibilidade de etapa),
 * mas o reducer opera no array completo — sem esse par o drag moveria o
 * fornecedor errado sempre que houvesse linha oculta.
 *
 * As duas visões (plana e agrupada) consomem este mesmo hook de propósito:
 * duplicar o filtro é o caminho mais curto para elas divergirem.
 */
export function useVisibleSupplierRows(): VisibleSupplierRow[] {
  const { activeCalcOption, versionInfo } = useBudgetMeta();
  const { supplierList } = useBudgetSuppliersState();
  const { budgetServices } = useBudgetServicesState();

  return useMemo(() => {
    return (supplierList ?? [])
      .map((el, absoluteIndex) => ({ el, absoluteIndex }))
      .filter(({ el }) => {
        if (el.calcOption !== activeCalcOption) return false;
        // A visibilidade por etapa vem de `service.versions` — a `supplierList` é
        // compartilhada por todas as etapas e não pode ser podada (ver
        // DELETE_SUPPLIER em BudgetContext/reducer-funcion.ts).
        return isSupplierVisibleInVersion(
          budgetServices,
          el.supplierId,
          activeCalcOption,
          versionInfo?.version ?? 0,
          el.deletedInVersions,
          versionInfo?.status
        );
      });
  }, [
    supplierList,
    budgetServices,
    activeCalcOption,
    versionInfo?.version,
    versionInfo?.status,
  ]);
}
